import { useCallback, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui/button";
import { Download, Plus } from "lucide-react";
import { toast } from "@patient-watch/ui/sonner";
import { LeadGenFilterSelect } from "@/features/lead-gen/components/LeadGenFilterSelect";
import { LeadGenFiltersDialog } from "@/features/lead-gen/components/LeadGenFiltersDialog";
import { LeadGenNavTabs, LeadGenToolbarSearch } from "@/features/lead-gen/components/LeadGenNavTabs";
import { LeadsTable } from "@/features/lead-gen/components/LeadsTable";
import {
    compactFilters,
    leadFilterCount,
    NO_TEAM_FILTER,
    parseLeadListSearch,
    REPLY_OUTCOME_FILTER_OPTIONS,
    type LeadListFilters,
} from "@/features/lead-gen/leadGenFilters";
import { useLeadGen } from "@/features/lead-gen/LeadGenContext";
import { LEAD_EMAIL_STATUS_OPTIONS, LEAD_STATUS_OPTIONS } from "@/features/lead-gen/types";

export const Route = createFileRoute("/_app/sales/leads/")({
    component: LeadsIndexPage,
    validateSearch: parseLeadListSearch,
});

function buildStringOptions(values: string[]) {
    return Array.from(new Set(values.filter(Boolean)))
        .toSorted((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        .map((value) => ({ value, label: value }));
}

function LeadsIndexPage() {
    const filters = Route.useSearch();
    const navigate = useNavigate({ from: "/sales/leads/" });
    const { leads, setLeads, companies, addCompany, setCompanyTeams, exportSql } = useLeadGen();
    const [searchQuery, setSearchQuery] = useState("");

    const setFilters = (next: LeadListFilters) => void navigate({ search: next, replace: true });
    const patchFilters = (patch: LeadListFilters) => setFilters(compactFilters({ ...filters, ...patch }));

    const companyFilterOptions = useMemo(
        () => buildStringOptions([...companies.map((company) => company.name), ...leads.map((lead) => lead.company)]),
        [companies, leads]
    );
    const teamFilterOptions = useMemo(() => {
        const teams = buildStringOptions([
            ...companies.flatMap((company) => company.teams),
            ...leads.map((lead) => lead.team),
        ]);
        return [{ value: NO_TEAM_FILTER, label: "No specialty" }, ...teams];
    }, [companies, leads]);

    const handleAddCompany = useCallback(
        (name: string) => {
            addCompany(name);
        },
        [addCompany]
    );

    const handleAddCompanyTeam = useCallback(
        (companyName: string, team: string) => {
            const company = companies.find(
                (item) => item.name.localeCompare(companyName, undefined, { sensitivity: "base" }) === 0
            );
            if (!company) {
                addCompany(companyName, [team]);
                return;
            }
            if (company.teams.some((item) => item.localeCompare(team, undefined, { sensitivity: "base" }) === 0)) {
                return;
            }
            setCompanyTeams(company.company_id, [...company.teams, team]);
        },
        [addCompany, companies, setCompanyTeams]
    );

    return (
        <>
            <LeadGenNavTabs
                toolbar={
                    <>
                        <LeadGenFiltersDialog
                            activeCount={leadFilterCount(filters)}
                            onClear={() => setFilters({})}
                            title="Filter leads"
                        >
                            <LeadGenFilterSelect
                                id="leads-filter-company"
                                label="Company"
                                value={filters.company ?? ""}
                                onChange={(company) => patchFilters({ company })}
                                options={companyFilterOptions}
                                allLabel="All companies"
                            />
                            <LeadGenFilterSelect
                                id="leads-filter-team"
                                label="Specialty"
                                value={filters.team ?? ""}
                                onChange={(team) => patchFilters({ team })}
                                options={teamFilterOptions}
                                allLabel="All specialties"
                            />
                            <LeadGenFilterSelect
                                id="leads-filter-status"
                                label="Status"
                                value={filters.status ?? ""}
                                onChange={(status) => patchFilters({ status: status as LeadListFilters["status"] })}
                                options={LEAD_STATUS_OPTIONS}
                                allLabel="All statuses"
                            />
                            <LeadGenFilterSelect
                                id="leads-filter-email"
                                label="Email"
                                value={filters.email ?? ""}
                                onChange={(email) => patchFilters({ email: email as LeadListFilters["email"] })}
                                options={LEAD_EMAIL_STATUS_OPTIONS}
                                allLabel="All emails"
                            />
                            <LeadGenFilterSelect
                                id="leads-filter-reply"
                                label="Reply"
                                value={filters.reply ?? ""}
                                onChange={(reply) => patchFilters({ reply: reply as LeadListFilters["reply"] })}
                                options={REPLY_OUTCOME_FILTER_OPTIONS}
                                allLabel="All replies"
                            />
                        </LeadGenFiltersDialog>
                        <LeadGenToolbarSearch onDebouncedChange={setSearchQuery} />
                    </>
                }
                action={
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="min-h-0 shrink-0"
                            onClick={() => {
                                exportSql();
                                toast.success("SQL export downloaded");
                            }}
                        >
                            <Download className="w-3.5 h-3.5" />
                            Export SQL
                        </Button>
                        <Button type="button" size="sm" className="min-h-0 shrink-0" asChild>
                            <Link to="/sales/leads/new">
                                <Plus className="w-3.5 h-3.5" />
                                Add Lead
                            </Link>
                        </Button>
                    </div>
                }
            />

            <LeadsTable
                leads={leads}
                companies={companies}
                filters={filters}
                onFiltersChange={setFilters}
                searchQuery={searchQuery}
                onLeadsChange={setLeads}
                onAddCompany={handleAddCompany}
                onAddCompanyTeam={handleAddCompanyTeam}
            />
        </>
    );
}
